import { perkAtk } from "./perkAtk";
import { perkHp } from "./perkHp";
import { perkLifesteal } from "./perkLifesteal";
import { perkArmor } from "./perkArmor";
import { perkThorns } from "./perkThorns";
import { perkAdrenaline } from "./perkAdrenaline";
import { perkRage } from "./perkRage";
import { perkMultiAttack } from "./perkMultiAttack";
import { perkBossReflect } from "./perkBossReflect";
import { perkBossHeal } from "./perkBossHeal";
import type { PerkDefinition, ModifierContext } from "./types";

const PERK_LIST = [
  perkHp,
  perkAtk,
  perkLifesteal,
  perkArmor,
  perkThorns,
  perkAdrenaline,
  perkRage,
  perkMultiAttack,
  perkBossReflect,
  perkBossHeal,
] as const;

export type PerkId = (typeof PERK_LIST)[number]["id"];

export type PerkMap = Record<PerkId, PerkDefinition<PerkId>>;

export const PERKS = PERK_LIST.reduce((acc, perk) => {
  acc[perk.id] = perk;
  return acc;
}, {} as PerkMap);

/**
 * Apply multiple perks to an actor (player or enemy) in sequence.
 * Works with any actor that has maxHp, hp, atk, and armor properties.
 */
export const applyPerks = <
  TActor extends { maxHp: number; hp: number; atk: number; armor: number },
>(
  actor: TActor,
  perkIds: PerkId[]
): void => {
  for (const perkId of perkIds) {
    const perk = PERKS[perkId];
    if (!perk) {
      continue;
    }

    const context: ModifierContext = {
      addMaxHp: (amount: number) => {
        actor.maxHp += amount;
      },
      healActor: (amount: number) => {
        actor.hp = Math.min(actor.hp + amount, actor.maxHp);
      },
      addAttack: (amount: number) => {
        actor.atk += amount;
      },
      addArmor: (amount: number) => {
        actor.armor += amount;
      },
    };

    perk.apply(context);
  }
};

export type { PerkApplyContext, PerkDefinition, ModifierContext } from "./types";
